import { useState, useRef } from 'react';
import './MultiViewWidget.css';
import {useEffect} from 'react';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from 'styled-components';

interface Props {
  text: string;
  onClose: () => void;
  title?: string;
  loadingPrefix?: string;
  disclaimer?: string;
}

function SummaryStarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ marginLeft: '4px' }}>
      <path
        d="M12 2L14.2 9.8L22 12L14.2 14.2L12 22L9.8 14.2L2 12L9.8 9.8L12 2Z"
        fill="#196FE1"
      />
      <path
        d="M18 4L18.8 6.8L21.5 7.5L18.8 8.2L18 11L17.2 8.2L14.5 7.5L17.2 6.8L18 4Z"
        fill="#4DA3FF"
      />
    </svg>
  );
}

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => (theme.darkMode ?  "rgba(255, 255, 255, 0.13)" : "rgba(0, 0, 0, 0.2)")};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10999;
`;

export const Popup = styled.div`
  background-color: ${({ theme }) => (theme.darkMode ? "#1A1A1A" : "#fff" )};
  padding: 20px;
  width: 520px;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  color: ${({ theme }) => (theme.darkMode ? "#E0E0E0" : "#333")};
`;

export const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const PopupTitle = styled.h3`
  display: flex;
  align-items: center;
  margin: 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 20px;
  line-height: 1;
  color: ${({ theme }) => (theme.darkMode ? "#E0E0E0" : "#333")};
`;

export const InsightPopup = ({
  text,
  onClose,
  title = "Data Insights",
  loadingPrefix = "Thinking",
  disclaimer = "*AI-generated insights based on the data available in this dashboard. AI can make mistakes.",
}: Props) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [displayedText, setDisplayedText] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
  const loadingText = "Loading";

  if (text !== loadingText) {
    setDisplayedText(text);
    setShowDisclaimer(true);
    return;
  }

  let dotCount = 0;
  const maxDots = 15;

  const interval = setInterval(() => {
    dotCount = (dotCount + 1) % (maxDots + 1); 
    setDisplayedText(loadingPrefix + ".".repeat(dotCount));
  }, 200);

  return () => clearInterval(interval);
}, [text, loadingPrefix]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <Overlay>
      <Popup ref={popupRef}>
        <PopupHeader>
          <PopupTitle>
            {title}
            <SummaryStarIcon />
          </PopupTitle>
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            &times;
          </CloseButton>
        </PopupHeader>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h3: ({ children }) => (
              <h3 style={{ marginTop: 0, marginBottom: "12px" }}>{children}</h3>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: "6px" }}>{children}</li>
            ),
            p: ({ children }) => (
              <p style={{ marginBottom: "10px" }}>{children}</p>
            ),
            strong: ({ children }) => (
              <strong style={{ fontWeight: 600 }}>{children}</strong>
            )
          }}
        >
          {displayedText}
        </ReactMarkdown>
        {showDisclaimer &&
        <p style={{ opacity: 0.6, fontSize: "10px", marginTop: "20px" }}>
          {disclaimer}
        </p>}
      </Popup>
    </Overlay>
  );
};